import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail } from 'lucide-react';
import type { ChildOrder, Order } from '@/apis/orders';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSendDocumentToCustomer } from '@/hooks/useAdminOrders';

// Mirrors the backend fallback (order.service DEFAULT_DOCUMENT_REQUEST_MESSAGE)
// so the admin sees exactly what the customer receives.
const DEFAULT_REQUEST_MESSAGE =
  'To process your order, we kindly require the following documents:\n\n' +
  '1. Credit Card Authorization Form\n' +
  '2. Copy of Credit Card (both front and back)\n' +
  '3. Photo ID of the cardholder\n\n' +
  'Please upload them using the UPLOAD DOCUMENTS button below.';

const requestDocumentSchema = z.object({
  message: z.string().trim().min(1, 'Message is required'),
});

type RequestDocumentForm = z.infer<typeof requestDocumentSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | ChildOrder;
  /** Prefill for the editable message (e.g. a document-specific request). */
  defaultMessage?: string;
}

export function RequestDocumentDialog({
  open,
  onOpenChange,
  order,
  defaultMessage,
}: Props) {
  const sendDocument = useSendDocumentToCustomer(order.id);
  const customerEmail = 'customer' in order ? order.customer.email : undefined;
  const initialMessage = defaultMessage ?? DEFAULT_REQUEST_MESSAGE;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RequestDocumentForm>({
    resolver: zodResolver(requestDocumentSchema),
    defaultValues: { message: initialMessage },
  });

  useEffect(() => {
    if (open) reset({ message: initialMessage });
  }, [open, reset, initialMessage]);

  const onSubmit = handleSubmit((data) => {
    sendDocument.mutate(
      { message: data.message },
      { onSuccess: () => onOpenChange(false) }
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[520px] flex-col overflow-hidden p-0">
        <DialogHeader className="flex-shrink-0 border-b border-smoke px-6 pt-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-black">
            Request Documents
          </DialogTitle>
          <p className="mt-1 text-sm font-normal text-lightGray">
            Ask the customer to upload the documents needed to verify this
            order.
          </p>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="mb-4 flex items-start gap-3 rounded-xl bg-info-light px-4 py-3">
              <Mail size={16} className="mt-0.5 shrink-0 text-info" />
              <p className="text-sm font-normal text-black">
                An email with your message, a link to download the Credit Card
                Authorization Form and an <b>UPLOAD DOCUMENTS</b> button will
                be sent to{' '}
                <b>{customerEmail || 'the customer'}</b>. The order
                status will change to <b>Request Document</b>. Once the
                customer uploads, review the files via <b>Review Request</b>.
              </p>
            </div>

            <label
              htmlFor="request-document-message"
              className="mb-2 block text-base font-semibold text-black"
            >
              Message to customer
            </label>
            <div className="rounded-xl border border-smoke bg-smoke2 focus-within:border-primary-s500">
              <textarea
                id="request-document-message"
                className="w-full resize-none border-0 bg-transparent px-3 py-3 text-sm text-black placeholder:text-lightGray outline-none focus:ring-0"
                rows={8}
                placeholder="Describe the documents you need from the customer"
                {...register('message')}
              />
            </div>
            {errors.message && (
              <p className="mt-1 text-xs font-normal text-danger">
                {errors.message.message}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-smoke px-6 py-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full border border-smoke bg-smoke1 px-6 py-2 text-sm font-normal text-black transition-colors hover:bg-smoke"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sendDocument.isPending}
              className="rounded-full bg-primary-s500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {sendDocument.isPending ? 'Sending…' : 'Send Request Email'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
